import { ReadonlyURLSearchParams } from 'next/navigation';

export function formatHour(hour: number) {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? "AM" : "PM";
  return `${h}:00 ${ampm}`;
}

// Helper to format date as UTC ISO string (YYYY-MM-DDTHH:mm:ss.sssZ) using local values
export const toLocalISOString = (date: Date) => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds())
  );
}

// Returns params.toString() with 'from' and 'to' set, using range if available, else now/tomorrow
export function getOrDefaultFromTo(
  range?: { startDate: Date; endDate: Date }[],
  params?: URLSearchParams
) {
  const searchParams = params ? new URLSearchParams(params) : new URLSearchParams();
  let from = searchParams.get('from');
  let to = searchParams.get('to');
  if (!from || !to) {
    if (range && range[0]) {
      from = toLocalISOString(range[0].startDate);
      to = toLocalISOString(range[0].endDate);
    } else {
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      from = toLocalISOString(now);
      to = toLocalISOString(tomorrow);
    }
    searchParams.set('from', from);
    searchParams.set('to', to);
  }
  return searchParams.toString();
}

// Ensures 'from' and 'to' are set in the given URLSearchParams, using range or fallback. Returns the mutated params.
export function ensureFromToParams(
  range?: { startDate: Date; endDate: Date }[],
  params?: URLSearchParams | ReadonlyURLSearchParams
): URLSearchParams {
  
  // Create a mutable copy of the params
  const searchParams = params ? new URLSearchParams(params) : new URLSearchParams();
  let from = searchParams.get('from');
  let to = searchParams.get('to');
  if (!from || !to) {
    if (range && range[0]) {
      from = toLocalISOString(range[0].startDate);
      to = toLocalISOString(range[0].endDate);
    } else {
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      from = toLocalISOString(now);
      to = toLocalISOString(tomorrow);
    }
    searchParams.set('from', from);
    searchParams.set('to', to);
  }
  return searchParams;
}

export function formatFullDateTime(dateString: string) {
  if (!dateString) return "";
  // Parse as local time to avoid timezone conversion issues
  // Handle format like "2025-08-19T08:00:00.000Z" or "2025-08-19T08:00:00"
  let date: Date;
  try {
    const [datePart, timePart] = dateString.split('T');
    if (datePart && timePart) {
      const [year, month, day] = datePart.split('-').map(Number);
      // Remove milliseconds and timezone info if present
      const cleanTimePart = timePart.split('.')[0].split('+')[0].split('Z')[0];
      const [hour, minute, second] = cleanTimePart.split(':').map(Number);
      date = new Date(year, month - 1, day, hour, minute || 0, second || 0);
    } else {
      // Fallback to standard parsing if format is unexpected
      date = new Date(dateString);
    }
  } catch {
    // Fallback to standard parsing on error
    date = new Date(dateString);
  }
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
}

export const formatDateTimeWithoutYear = (dateString: string) => {
  if (!dateString) return "";
  // Parse as local time to avoid timezone conversion issues
  let date: Date;
  try {
    const [datePart, timePart] = dateString.split('T');
    if (datePart && timePart) {
      const [year, month, day] = datePart.split('-').map(Number);
      // Remove milliseconds and timezone info if present
      const cleanTimePart = timePart.split('.')[0].split('+')[0].split('Z')[0];
      const [hour, minute, second] = cleanTimePart.split(':').map(Number);
      date = new Date(year, month - 1, day, hour, minute || 0, second || 0);
    } else {
      // Fallback to standard parsing if format is unexpected
      date = new Date(dateString);
    }
  } catch {
    // Fallback to standard parsing on error
    date = new Date(dateString);
  }
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day} ${month} ${hours}:${minutes} ${ampm}`;
};